import { Button } from "@/components/ui/button";
import { ArrowRight, Check, Sparkles } from "lucide-react";
import { Link } from "react-router-dom";
import { useTranslation } from "@/i18n";

const CTA = () => {
  const { t } = useTranslation();

  const benefits = [
    t('cta.benefit1'),
    t('cta.benefit2'),
    t('cta.benefit3'),
  ];

  const stats = [
    { value: "850+", label: t('cta.statStores') },
    { value: "120K+", label: t('cta.statProducts') },
    { value: "14", label: t('cta.statLanguages') },
    { value: "95+", label: t('cta.statScore') },
  ];

  return (
    <section className="py-20 md:py-32 relative overflow-hidden bg-primary">
      {/* Radial glow */}
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[800px] rounded-full bg-white/5 blur-3xl" />
      </div>
      <div className="absolute inset-0 grid-pattern opacity-10" />

      <div className="container mx-auto px-4 relative">
        <div className="max-w-3xl mx-auto text-center">
          {/* Badge */}
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/10 backdrop-blur-sm text-white text-sm font-medium mb-8 border border-white/20">
            <Sparkles size={16} />
            <span>{t('cta.badge')}</span>
          </div>

          <h2 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold text-white mb-4">
            {t('cta.urgencyTitle')}
          </h2>
          <p className="text-xl sm:text-2xl md:text-3xl font-bold mb-6 text-white/90">
            {t('cta.urgencyHighlight')}
          </p>
          <p className="text-lg text-white/80 mb-6 max-w-2xl mx-auto">
            {t('cta.urgencySubtitle')}
          </p>

          {/* Launch offer */}
          <div className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-white/15 backdrop-blur-sm text-white text-sm font-semibold mb-8 border border-white/25">
            {t('cta.launchOffer')}
          </div>

          {/* Benefits */}
          <div className="flex flex-wrap items-center justify-center gap-x-8 gap-y-3 mb-10">
            {benefits.map((benefit, i) => (
              <div key={i} className="flex items-center gap-2 text-white/90">
                <div className="w-5 h-5 rounded-full bg-white/20 flex items-center justify-center">
                  <Check size={12} className="text-white" />
                </div>
                <span className="text-sm font-medium">{benefit}</span>
              </div>
            ))}
          </div>

          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <Link to="/auth?mode=signup&plan=pro&period=annual" className="w-full sm:w-auto">
              <Button 
                size="xl" 
                className="w-full sm:w-auto bg-white text-primary hover:bg-white/90 shadow-elevated group font-bold"
              >
                {t('cta.button')}
                <ArrowRight size={20} className="transition-transform group-hover:translate-x-1" />
              </Button>
            </Link>
          </div>

          <p className="mt-6 text-sm text-white/80 font-medium">
            {t('cta.urgencyNote')}
          </p>
          <p className="mt-4 text-sm text-white/80">
            {t('cta.hasAccount')} <Link to="/auth" className="text-white hover:underline">{t('cta.login')}</Link>
          </p>
        </div>

        {/* Stats bar */}
        <div className="mt-16 pt-8 border-t border-white/10">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6 max-w-3xl mx-auto">
            {stats.map((stat, i) => (
              <div key={i} className="text-center">
                <p className="text-2xl font-extrabold text-white">{stat.value}</p>
                <p className="text-xs text-white/50 mt-1">{stat.label}</p>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default CTA;
